import { DATE_INPUT_FORMAT, TODAY, TODAY_EOD } from '@/constants';
import { Datetime, saveFile } from '@/utils';
import { ServiceIssueFiltersType, useServiceIssueFilters } from '@/hooks/useServiceIssueFilters';
import { useCallback, useEffect, useState } from 'react';

import AirlineIataDropdown from '@/components/AirlineDropdown';
import AirportDropdown from '@/components/AirportDropdown';
import { Button } from 'react-bootstrap';
import DateRangePicker from '@/components/DateRangePicker';
import FormButton from '@/components/FormButton';
import { InputWithIcon } from '@/components/Input';
import LostAndFoundHistoryFiltersInfo from '@/features/LostAndFoundHistory/components/LostAndFoundHistoryFilters/LostAndFoundHistoryFiltersInfo';
import LostAndFoundIssueReturnStatusDropdown from '@/features/LostAndFoundHistory/components/LostAndFoundHistoryFilters/LostAndFoundIssueReturnStatusDropdown';
import PageNavigationDropdown from '@/common/PageNavigationDropdown';
import QueryFilters from '@/components/QueryFilters';
import { deleteLostAndFoundIssue } from '@/features/LostAndFoundHistory/api/services/deleteLostAndFoundIssue';
import { deleteLostAndFoundIssueDocument } from '../../api/services/deleteLostAndFoundIssueDocument';
import { downloadLostAndFoundIssueWithCacheCsv } from '@/features/LostAndFoundHistory/api/services/downloadLostAndFoundIssueCSV';
import useConfirmation from '@/hooks/useConfirmation';
import { useLostAndFoundHistory } from '@/features/LostAndFoundHistory';
import usePopState from '@/hooks/usePopState';

const initLostAndFoundHistoryFiltersState: ServiceIssueFiltersType = {
  startDatetime: TODAY,
  endDatetime: TODAY_EOD,
  airportCode: [],
  returnStatuses: [],
  tripServicerIataAirlineCodes: [],
};

const LostAndFoundHistoryFilters = (): JSX.Element => {
  const [{ loading, selected, reports, search }, setState, { submitFilters }] = useLostAndFoundHistory(
    ({ loading, selected, reports, search }) => ({
      loading,
      selected,
      reports,
      search,
    })
  );
  const filtersState = useServiceIssueFilters(({ state }) => state);
  const setFiltersState = useServiceIssueFilters(({ setState }) => setState);
  const saveFiltersState = useServiceIssueFilters(({ saveState }) => saveState);
  const rehydrateFiltersState = useServiceIssueFilters(({ rehydrate }) => rehydrate);
  const [lastQuery, setLastQuery] = useState<ServiceIssueFiltersType>(filtersState);
  const confirmDelete = useConfirmation();

  const handleChange = useCallback(
    (name: string) =>
      (value: unknown): void =>
        setFiltersState((current) => ({ ...current, [name]: value })),
    [setFiltersState]
  );

  const handleSubmit = useCallback((): void => {
    console.log('IncidentHistoryFilters: Submitting...');
    // get the latest state from closure to ensure it's always accurate
    const { state } = useServiceIssueFilters.getState();
    saveFiltersState();
    setLastQuery(state);
    submitFilters(state);
  }, [filtersState, saveFiltersState]);

  const handleReset = useCallback((): void => {
    const startDatetime = new Datetime().startOf('day').toString();
    const endDatetime = new Datetime().endOf('day').toString();
    const resetInput = { ...initLostAndFoundHistoryFiltersState, startDatetime, endDatetime };
    setFiltersState(resetInput);
    setLastQuery(resetInput);
    setState((current) => ({ ...current, search: '' }));
    saveFiltersState();
    submitFilters(resetInput);
  }, [setState, saveFiltersState]);

  const handlePopState = useCallback(() => rehydrateFiltersState().then(handleSubmit), [rehydrateFiltersState, handleSubmit]);
  usePopState(handlePopState);

  const onExportToCSV = async () => {
    const { state: csvExportFilters } = useServiceIssueFilters.getState();
    const res = await downloadLostAndFoundIssueWithCacheCsv([csvExportFilters]);
    if (!res) return;
    const blob = new Blob([res.data], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    saveFile(url, `LostAndFoundIssueHistory-${new Datetime().format(DATE_INPUT_FORMAT)}.csv`);
  };

  const onDelete = () =>
    confirmDelete()
      .then(async () => {
        try {
          // Get all of the documents and delete them first
          const documentIds = Array.from(selected.values())
            .map((issue) =>
              issue.documents.map((document) => ({
                id: document.id,
              }))
            )
            .flat();
          if (documentIds?.length > 0) {
            await deleteLostAndFoundIssueDocument(documentIds);
          }
          const ids = Array.from(selected.keys()).map((id) => ({ id }));
          await deleteLostAndFoundIssue(ids);
          setState((current) => ({
            ...current,
            selected: new Map(),
          }));
        } catch (err) {
          console.error('Error Deleting Lost and Found.', err);
        } finally {
          return submitFilters?.(useServiceIssueFilters.getState().state);
        }
      })
      .catch(() => console.log('Cancelled delete.'));
  const onOpenSettings = () => {
    console.log('Open Settings');
  };

  useEffect(() => {
    handleSubmit();
  }, []);

  return (
    <>
      <QueryFilters className="LostAndFoundHistoryFilters justify-content-between">
        <div className="d-flex gap-2">
          <QueryFilters.Control>
            <DateRangePicker
              name="dates"
              isDirty={filtersState.startDatetime !== lastQuery.startDatetime || filtersState.endDatetime !== lastQuery.endDatetime}
              value={[filtersState.startDatetime, filtersState.endDatetime]}
              onChange={(event) => {
                const [from, to] = event.target.value.split(' - ') || [null, null];
                handleChange('startDatetime')(from);
                handleChange('endDatetime')(to);
              }}
            />
          </QueryFilters.Control>
          <QueryFilters.Input>
            <AirportDropdown.Multi
              value={filtersState.airportCode}
              onChange={handleChange('airportCode')}
              options={{ locale: { 'Select...': 'Airports' } }}
            />
          </QueryFilters.Input>
          <QueryFilters.Input>
            <AirlineIataDropdown.Multi
              value={filtersState.tripServicerIataAirlineCodes}
              onChange={handleChange('tripServicerIataAirlineCodes')}
            />
          </QueryFilters.Input>
          <QueryFilters.Input>
            <LostAndFoundIssueReturnStatusDropdown value={filtersState.returnStatuses} onChange={handleChange('returnStatuses')} />
          </QueryFilters.Input>
          <QueryFilters.Control>
            <Button className="border-white" name="SUBMIT" variant="success" onClick={handleSubmit} disabled={loading}>
              {loading && <i className="fa fa-spinner fa-pulse" />}
              {!loading && <span>Go</span>}
            </Button>
            <Button name="RESET" variant="outline-secondary-subtle" onClick={handleReset} disabled={loading}>
              Reset
            </Button>
          </QueryFilters.Control>
          <QueryFilters.Control>
            <InputWithIcon
              icon={<i className="sv sv-magnifier fs-4" />}
              name="search"
              value={search || ''}
              onChange={(value) => {
                setState((current) => ({ ...current, search: value }));
              }}
              placeholder="Search"
            />
          </QueryFilters.Control>
        </div>
        <div className="d-flex justify-content-end gap-2">
          <QueryFilters.Input>
            <PageNavigationDropdown />
          </QueryFilters.Input>
          <QueryFilters.Control>
            <FormButton
              icon={<i className="sv sv-trash2 {font-size:1.5rem;}" />}
              name="DELETE_LOST_AND_FOUND"
              variant="outline-secondary-subtle"
              onClick={onDelete}
              disabled={selected.size === 0}
            />
          </QueryFilters.Control>
          <QueryFilters.Control>
            <FormButton
              icon={<i className="sv sv-excel {font-size:1.5rem;}" />}
              name="EXPORT_AS_CSV"
              variant="outline-secondary-subtle"
              onClick={onExportToCSV}
              feedback="Export to CSV"
              disabled={reports.size === 0}
            />
          </QueryFilters.Control>
          {/* <QueryFilters.Control>
            <FormButton
              variant="outline-secondary-subtle"
              onClick={onOpenSettings}
              icon={<i className="sv sv-cog {font-size:1.5rem;}" />}
              feedback="Lost and Found History Page Settings & Preferences"
            />
          </QueryFilters.Control> */}
        </div>
      </QueryFilters>
      <LostAndFoundHistoryFiltersInfo />
    </>
  );
};

export default LostAndFoundHistoryFilters;
